import { useState } from 'react';

import {
  type IotStatus,
  type BaseStationConfig,
  type HardwareVersion,
  type SoftwareVersion,
  type Timestamp,
  type Brightness,
  type Temperature,
  type Fan,
  type Occupancy,
  type OccupancyLastUpdated,
  type BacklightColor,
  type BacklightPattern,
  type ScreenPower,
  type WifiNetworksResponse,
  type WifiStatusApiResponse,
} from '../hooks/firmware';

// Build-time environment variable to enable firmware override
const FIRMWARE_OVERRIDE_ENABLED = process.env.APP_FIRMWARE_OVERRIDE === 'true';

// Default override values - can be customized per environment
const DEFAULT_FIRMWARE_OVERRIDES = {
  // Configuration endpoints
  iotStatus: {
    connected: true,
    hub_id: process.env.APP_OVERRIDE_HUB_ID || '42c8e89a-5dc9-4adf-a439-453952254195',
    last_connection: new Date().toISOString(),
    provisioned: true,
  } as IotStatus,

  baseStationConfig: {
    device_name: process.env.APP_OVERRIDE_DEVICE_NAME || 'HomeGuardian Hub',
    lock_enabled: false,
    lock_pin: '0000',
    setup_mode: false,
    serial_number: process.env.APP_OVERRIDE_SERIAL_NUMBER || 'HG-DEV-001',
  } as BaseStationConfig,

  hardwareVersion: {
    version: '1.0.0',
  } as HardwareVersion,

  softwareVersion: {
    version: '1.0.0-dev',
  } as SoftwareVersion,

  timestamp: {
    current_time: new Date().toISOString(),
  } as Timestamp,

  // Display control endpoints
  brightness: {
    brightness: 80,
  } as Brightness,

  temperature: {
    temperature: 22.5,
    unit: 'Celsius',
  } as Temperature,

  fan: {
    fan_speed: 10,
  } as Fan,

  occupancy: {
    occupied: true,
  } as Occupancy,

  occupancyLastUpdated: {
    last_updated: new Date(Date.now() - 5 * 60 * 1000).toISOString(), // 5 minutes ago
  } as OccupancyLastUpdated,

  backlightColor: {
    color: 'White',
    custom_rgb: { r: 255, g: 255, b: 255 },
  } as BacklightColor,

  backlightPattern: {
    pattern: 'Solid',
  } as BacklightPattern,

  screenPower: {
    power: true,
  } as ScreenPower,

  // WiFi endpoints
  wifiNetworks: {
    networks: [
      { ssid: 'HomeGuardian_Test', signal: 85, secure: true },
      { ssid: 'Guest_Network', signal: 70, secure: false },
      { ssid: 'Office_WiFi', signal: 92, secure: true },
    ],
  } as WifiNetworksResponse,

  wifiStatus: {
    status: {
      interface: 'wlan0',
      ip: '192.168.1.100',
      ssid: 'HomeGuardian_Test',
      status: 'connected',
    },
  } as WifiStatusApiResponse,
};

export interface FirmwareOverrides {
  // Configuration overrides
  iotStatus?: IotStatus;
  baseStationConfig?: BaseStationConfig;
  hardwareVersion?: HardwareVersion;
  softwareVersion?: SoftwareVersion;
  timestamp?: Timestamp;

  // Display control overrides
  brightness?: Brightness;
  temperature?: Temperature;
  fan?: Fan;
  occupancy?: Occupancy;
  occupancyLastUpdated?: OccupancyLastUpdated;
  backlightColor?: BacklightColor;
  backlightPattern?: BacklightPattern;
  screenPower?: ScreenPower;

  // WiFi overrides
  wifiNetworks?: WifiNetworksResponse;
  wifiStatus?: WifiStatusApiResponse;
}

export function isFirmwareOverrideEnabled(): boolean {
  return FIRMWARE_OVERRIDE_ENABLED;
}

export function getFirmwareOverrides(): FirmwareOverrides {
  return DEFAULT_FIRMWARE_OVERRIDES;
}

export function createMockAsyncState<T>(initialData: T) {
  const [data, setData] = useState<T>(initialData);

  return {
    data,
    loading: false,
    error: null,
    execute: async () => {},
    setData,
  };
}

// Helper function to check if a specific endpoint should be overridden
export function shouldOverrideEndpoint(endpoint: string): boolean {
  if (!FIRMWARE_OVERRIDE_ENABLED) return false;

  // Map of endpoints to their override availability
  const overrideableEndpoints = [
    '/iot/status',
    '/config/base-station',
    '/hw_version',
    '/sw_version',
    '/timestamp',
    '/brightness',
    '/temperature',
    '/fan',
    '/occupancy',
    '/occupancy/last-updated',
    '/backlight/color',
    '/backlight/pattern',
    '/screen-power',
    '/wifi/networks',
    '/wifi/status',
    '/wifi/connect',
    '/wifi/disconnect',
    '/wifi/save',
    '/wifi/forget',
  ];

  return overrideableEndpoints.includes(endpoint);
}

// Helper function to get override data for a specific endpoint
export function getEndpointOverride(endpoint: string): any {
  const overrides = getFirmwareOverrides();

  const endpointMap: Record<string, any> = {
    '/iot/status': overrides.iotStatus,
    '/config/base-station': overrides.baseStationConfig,
    '/hw_version': overrides.hardwareVersion,
    '/sw_version': overrides.softwareVersion,
    '/timestamp': overrides.timestamp,
    '/brightness': overrides.brightness,
    '/temperature': overrides.temperature,
    '/fan': overrides.fan,
    '/occupancy': overrides.occupancy,
    '/occupancy/last-updated': overrides.occupancyLastUpdated,
    '/backlight/color': overrides.backlightColor,
    '/backlight/pattern': overrides.backlightPattern,
    '/screen-power': overrides.screenPower,
    '/wifi/networks': overrides.wifiNetworks,
    '/wifi/status': overrides.wifiStatus,
  };

  return endpointMap[endpoint];
}

// Console logging for development
if (FIRMWARE_OVERRIDE_ENABLED) {
  console.log('🔧 Firmware Override Mode Enabled');
  console.log('Override values:', DEFAULT_FIRMWARE_OVERRIDES);
}
